import { omitBy, isNil } from 'lodash-es';
import { dateUtils, type MappedCity } from 'shared';

import { bloomreachLib } from '@/shared/lib';

import { BloomreachEventName } from './config';
import type { extractAnalyticsDataTypes } from '../analytics-data-extraction';

const getLocationInfo = (city: MappedCity, direction: 'to' | 'from') => {
  return {
    [`city_${direction}_iata`]: city.code,
    [`city_${direction}_name`]: city.name,
    [`city_${direction}_name_ru`]: city.name_ru,
    [`city_${direction}_name_kk`]: city.name_kk,
    [`city_${direction}_name_en`]: city.name_en,
    [`city_${direction}_country_iso_code`]: city.country.code,
    [`city_${direction}_country_name`]: city.country.name,
    [`city_${direction}_country_name_ru`]: city.country.name_ru,
    [`city_${direction}_country_name_kk`]: city.country.name_kk,
    [`city_${direction}_country_name_en`]: city.country.name_en,
  };
};

const mapPassengerFields = (
  params: extractAnalyticsDataTypes.ExtractSearchFormDataParams,
) => {
  const mappedParams = {
    ...params,
    passenger_adult: params.passenger_adt,
    passenger_youth: params.passenger_yth,
    passenger_child: params.passenger_chd,
    passenger_infant: params.passenger_inf,
  };

  delete mappedParams.passenger_adt;
  delete mappedParams.passenger_yth;
  delete mappedParams.passenger_chd;
  delete mappedParams.passenger_inf;

  return mappedParams;
};

type SearchFormParamsWithMappedPassengers = ReturnType<
  typeof mapPassengerFields
>;

const mapCities = (params: SearchFormParamsWithMappedPassengers) => {
  const mappedParams = {
    ...params,
    ...getLocationInfo(params.city_from, 'from'),
    ...getLocationInfo(params.city_to, 'to'),
  };
  delete mappedParams.city_from;
  delete mappedParams.city_to;

  return mappedParams;
};

type SearchFormParamsWithMappedCities = ReturnType<typeof mapCities>;

const formatParams = (params: SearchFormParamsWithMappedCities) => {
  return {
    ...params,
    date_from: dateUtils.toUnix(params.date_from),
    date_to: params.date_to ? dateUtils.toUnix(params.date_to) : undefined,
    travel_type: params.travel_type === 'I' ? 'international' : 'domestic',
    search_mode: params.search_mode.toLowerCase(),
  };
};

type FormattedSearchFormParams = ReturnType<typeof formatParams>;

export const searchButtonClick = (
  mappedParams: extractAnalyticsDataTypes.ExtractSearchFormDataParams,
  source: string,
) => {
  let mappedData: FormattedSearchFormParams & { search_click_source: string } =
    {
      ...formatParams(mapCities(mapPassengerFields(mappedParams))),
      search_click_source: source,
    };

  mappedData.class_type = mappedData.cabin_class;
  delete mappedData.cabin_class;

  // No need to send null values to Bloomreach
  mappedData = omitBy(mappedData, isNil);

  bloomreachLib.resetEventsState();
  bloomreachLib.track(BloomreachEventName.SEARCH_BUTTON_CLICK, mappedData);
};
